import { Elements, RecurlyProvider } from "@recurly/react-recurly";
import { inject } from "mobx-react";
import { useState, PropsWithChildren } from "react";
import { useLocation } from "react-router-dom";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import type { Store } from "@inferno/renderer-shared-core";

import { isPWSSessionBackend, PWSSessionBackend } from "@inferno/renderer-shared-core";
import { axiosLoaderWrapper } from "../../../lib/coastCommon";
import { recurlyLoader } from "../../../lib/recurly";
import "./CoastSignUp.style.scss";
import { CoastSignUpSubmitPassword } from "./CoastSignUpSubmitPassword.components";
import { CoastSubscribe } from "./CoastSubscribe.component";
import { CoastSubscriptionIntro } from "./CoastSubscriptionIntro.components";
import { CoastAlertDisplayError } from "../common/coast-alert/CoastAlertDisplay.component";
import { CoastSubscribeWrapper } from "./CoastSubscribeWrapper.component";

interface CoastSignUpProps {
  store?: Store;
}

interface CoastSignUpBaseProps {
  isGift: boolean;
}

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

function onCancel(session: PWSSessionBackend) {
  if (session.giftRecipient) {
    session.giftRecipient = null;
  }

  session.showPopUpMessage({
    title: "Are you sure you want to leave this page?",
    text: "The changes you made will not be saved",
    link: {
      text: "Continue",
      url: "/",
    },
  });
}

export const CoastSignUpBase = ({ children, isGift }: PropsWithChildren<CoastSignUpBaseProps>) => (
  <main className="component-coast-sign-up light-bg">
    <div className="signup-mobile-header" />

    <CoastSubscriptionIntro isLeftBlock={true} isGift={isGift} />
    <section className="sign-up-forms">{children}</section>
  </main>
);

export const CoastSignUp = inject("store")(({ store }: CoastSignUpProps) => {
  const session = store?.session.currentSession;
  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const query = useQueryParams();
  const [isGift, setIsGift] = useState(query.get("gift") === "true");
  const emailToken = query.get("emailToken");
  const handleIsGiftChange = (value: boolean) => {
    window.history.pushState(null, "", `/sign-up/?step=subscription&gift=${value}`);
    setIsGift(value);
  };

  if (emailToken) {
    return (
      <CoastSignUpBase isGift={isGift}>
        <CoastSignUpSubmitPassword onCancel={() => onCancel(session)} emailToken={emailToken} session={session} />
      </CoastSignUpBase>
    );
  }
  const step = query.get("step");

  if (step === "subscription") {
    return (
      <CoastSignUpBase isGift={isGift}>
        <CoastSubscribeWrapper
          onCancel={() => onCancel(session)}
          setIsGift={handleIsGiftChange}
          session={session}
          isGift={isGift}
        />
      </CoastSignUpBase>
    );
  }

  return (
    <CoastSignUpBase isGift={isGift}>
      <Remote loader={recurlyLoader} cacheKey={"recurly-lib"} showLoading={true}>
        {({ data: loaded }) =>
          loaded ? (
            <Remote
              loader={() => axiosLoaderWrapper(() => session.memberService.listSubscriptionPlans())}
              cacheKey={"coast-all-subscription-plans"}
              showLoading={true}
            >
              {({ data: availablePlans }) =>
                availablePlans ? (
                  <RecurlyProvider publicKey={store.env.COAST_RECURLY_PUBLIC_KEY}>
                    <Elements>
                      <CoastSubscribe
                        availablePlans={availablePlans}
                        setIsGift={setIsGift}
                        onCancel={() => onCancel(session)}
                        session={session}
                        isGift={isGift}
                        isAccountCreated={false}
                      />
                    </Elements>
                  </RecurlyProvider>
                ) : null
              }
            </Remote>
          ) : (
            <>
              <CoastAlertDisplayError isDisplayed={true} />
            </>
          )
        }
      </Remote>
    </CoastSignUpBase>
  );
});

export default CoastSignUp;
